import styled from '@emotion/styled';
import { useCallback, useEffect } from 'react';
import { colors, fonts, breakpoints, radii, FieldBox, StatusPill, CheckCircleIcon, useSessionUI } from '../../ui';
import { useSessionState } from '../../context/sessionStateContext';

// Shared layout pieces for the clinician test screens owned by the S3 redesign (Contrast
// Sensitivity, Refraction, Visual Field, Stroke Suite, Colour Vision). Presentation only.

/** Mirror the current test + sub step into the session state (BE-7) so a rejoining participant follows. */
export const useTestStepSync = (testKey, subStep) => {
  const { patch } = useSessionState();
  useEffect(() => {
    if (!testKey) return;
    Promise.resolve(patch({ step: 'test', test_key: testKey, sub_step: subStep || null })).catch(() => {});
  }, [patch, testKey, subStep]);
};

// Fills the panel body; every test step renders inside one.
export const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  gap: ${({ gap = 20 }) => gap}px;
  justify-content: ${({ center }) => (center ? 'center' : 'flex-start')};
  font-family: ${fonts.family};
  color: ${colors.text};
`;

// Two columns (preview | controls) that stack on phones.
export const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: ${({ cols = 'minmax(0, 1fr) minmax(0, 1fr)' }) => cols};
  gap: ${({ gap = 24 }) => gap}px;
  align-items: ${({ align = 'start' }) => align};
  width: 100%;
  @media (max-width: ${breakpoints.phone - 1}px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

export const Divider = styled.hr`
  width: 100%;
  margin: 0;
  border: 0;
  border-top: 1px solid ${colors.slate[200]};
`;

export const Label = styled.div`
  font-family: ${fonts.family};
  font-size: ${({ size = 13 }) => size}px;
  font-weight: ${({ weight = 600 }) => weight};
  color: ${({ tone }) => (tone === 'muted' ? colors.slate[400] : colors.slate[600])};
  text-align: ${({ align = 'left' }) => align};
  line-height: 1.35;
`;

export const Heading = styled.h3`
  margin: 0;
  font-family: ${fonts.family};
  font-size: 18px;
  font-weight: 700;
  color: ${colors.slate[600]};
  text-align: ${({ align = 'center' }) => align};
  @media (max-width: ${breakpoints.phone - 1}px) {
    font-size: 16px;
  }
`;

// Read-only value box ("RHN", "0.48", "52.0%").
export const ValueBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ align = 'flex-start' }) => align};
  min-height: 44px;
  min-width: ${({ minWidth = 72 }) => minWidth}px;
  padding: 0 12px;
  box-sizing: border-box;
  border: 1px solid ${colors.slate[200]};
  border-radius: ${radii.input}px;
  background: ${({ muted }) => (muted ? colors.slate[100] : '#fff')};
  color: ${({ muted }) => (muted ? colors.slate[400] : colors.slate[700])};
  font-family: ${({ mono }) => (mono ? "'OpticianSans', " + fonts.family : fonts.family)};
  font-size: ${({ fontSize = 15 }) => fontSize}px;
  font-weight: 600;
  letter-spacing: ${({ spaced }) => (spaced ? '0.12em' : 'normal')};
`;

export const LabelledValue = ({ label, children, ...rest }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }} {...rest}>
    <Label size={12}>{label}</Label>
    {children}
  </div>
);

// "Patient Distance [500mm]" read-only field + "Patient Positioned" pill.
export const PositionedPill = ({ positioned = true, children }) => (
  <StatusPill tone={positioned ? 'success' : 'warning'} style={{ height: 32, borderRadius: radii.button, whiteSpace: 'nowrap' }}>
    {positioned && <CheckCircleIcon size={14} />}
    {children || (positioned ? 'Patient Positioned' : 'Calculating…')}
  </StatusPill>
);

export const DistanceReadout = ({ distance, positioned = true, label = 'Patient Distance' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <Label size={13}>{label}</Label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <FieldBox readOnly value={distance ? `${Math.round(distance)}mm` : '--mm'} style={{ width: 104, minWidth: 0, height: 36 }} aria-label={label} />
      {positioned !== null && <PositionedPill positioned={positioned} />}
    </div>
  </div>
);

// Legend row with a coloured dot ("● Seen (0)").
const LegendRoot = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: ${fonts.family};
  li {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 15px;
    color: ${colors.slate[600]};
  }
  i {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    gap: 6px;
    li {
      font-size: 13px;
      gap: 8px;
    }
    i {
      width: 14px;
      height: 14px;
    }
  }
`;

/** @param items [{ color, label }] */
export const Legend = ({ items, ...rest }) => (
  <LegendRoot {...rest}>
    {items.map(({ color, label }) => (
      <li key={label}>
        <i style={{ background: color }} aria-hidden='true' />
        {label}
      </li>
    ))}
  </LegendRoot>
);

// "Confidence Level: 92%" label + teal progress bar.
const Bar = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${colors.slate[200]};
  overflow: hidden;
  div {
    height: 100%;
    border-radius: 4px;
    background: ${({ tone }) => tone};
    transition: width 300ms ease;
  }
`;

export const ConfidenceMeter = ({ percentage, label = 'Confidence Level' }) => {
  const pct = Math.max(0, Math.min(100, Number(percentage) || 0));
  const tone = pct < 30 ? colors.error[500] : pct < 50 ? colors.warning[500] : colors.primary;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }} data-testid='confidence-meter'>
      <Label size={14} weight={700}>{`${label}: ${percentage === '0' ? 0 : percentage}%`}</Label>
      <Bar tone={tone} role='progressbar' aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div style={{ width: `${pct}%` }} />
      </Bar>
    </div>
  );
};

// Small inline note (errors, hints) under a control group.
export const Note = styled.p`
  margin: 0;
  font-family: ${fonts.family};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ tone }) => (tone === 'error' ? colors.error[600] : colors.textMuted)};
  font-weight: ${({ tone }) => (tone === 'error' ? 700 : 400)};
  text-align: ${({ align = 'left' }) => align};
`;

// Bordered group used for option sets (Visual Field setup).
export const OptionCard = styled.fieldset`
  margin: 0;
  padding: 16px 18px;
  min-width: 0;
  border: 1px solid ${colors.slate[200]};
  border-radius: ${radii.card}px;
  background: #fff;
  legend {
    padding: 0 6px;
    margin-left: -6px;
    font-family: ${fonts.family};
    font-size: 13px;
    font-weight: 700;
    color: ${colors.slate[700]};
  }
`;

/**
 * useConfirmLeave - promise-based replacement for helpers/functions confirmNavigation() that uses the
 * IBISengage confirm dialog. Resolves only when the clinician answers Yes (same contract as before).
 *   const confirmLeave = useConfirmLeave();  confirmLeave().then(...)  |  confirmLeave('restart').then(...)
 */
export const useConfirmLeave = () => {
  const { confirm } = useSessionUI();
  return useCallback(
    (localRestart) =>
      new Promise((resolve) => {
        const opts = localRestart
          ? { message: 'Are you sure you want to restart this test?', detail: 'All results presented here will be lost.', tone: 'danger' }
          : { message: 'Leave this step?', detail: 'Navigating away from this page may mean that you lose test data.', tone: 'danger' };
        Promise.resolve(confirm(opts)).then((yes) => yes && resolve());
      }),
    [confirm],
  );
};
